import React, { useState, useEffect, useRef } from 'react';
import { useContracts } from '../../hooks/useContract';
import { formatTimestamp } from '../../utils/format';
import { shortenAddress } from '../../utils/wallet';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';

const DirectReferrals = ({ userId }) => {
  const { storage } = useContracts();
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const runIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId) return;
      const myRun = ++runIdRef.current;
      setLoading(true);
      try {
        const list = await storage.getUserReferrals(userId);
        if (cancelled || myRun !== runIdRef.current) return;
        const ids = list.map((x) => x.toNumber ? x.toNumber() : Number(x));

        const detailed = await Promise.all(ids.map(async (id) => {
          try {
            const u = await storage.users(id);
            return {
              id,
              wallet: u[0],
              joinTime: u[4],
              topPackageId: u[8].toNumber ? u[8].toNumber() : Number(u[8]),
              isBlocked: u[5]
            };
          } catch { return null; }
        }));
        if (cancelled || myRun !== runIdRef.current) return;
        setRefs(detailed.filter(Boolean));
      } catch (_) { /* ignore */ }
      finally {
        if (myRun === runIdRef.current) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return (
      <Card title="Direct Referrals">
        <div className="text-center" style={{ padding: 40 }}><MiniLoader /></div>
      </Card>
    );
  }

  if (refs.length === 0) {
    return (
      <Card title="Direct Referrals">
        <div className="empty-state">
          <div className="empty-state-icon">👥</div>
          <div>No referrals yet</div>
          <div style={{ fontSize: 12, marginTop: 8 }}>Share your referral link to grow your team</div>
        </div>
      </Card>
    );
  }

  return (
    <Card title={`Direct Referrals (${refs.length})`} glow>
      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Wallet</th>
              <th>Package</th>
              <th>Joined</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {refs.map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td className="font-mono" style={{ fontSize: 12 }}>{shortenAddress(r.wallet)}</td>
                <td>{r.topPackageId > 0 ? `P${r.topPackageId}` : '-'}</td>
                <td style={{ fontSize: 12 }}>{formatTimestamp(r.joinTime)}</td>
                <td>
                  {r.isBlocked ? (
                    <span className="badge badge-error">Blocked</span>
                  ) : (
                    <span className="badge badge-success">Active</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default DirectReferrals;
